import { isPlayedResult, pointsFor, type Colour } from '../model/types.js';
import { allocateColours, colourState } from './colour.js';
import {
  PairingError,
  type Pair,
  type PairingEngine,
  type PairingPlayer,
  type PairingRequest,
  type PairingResult,
} from './types.js';

interface Ranked {
  readonly p: PairingPlayer;
  readonly score: number;
  readonly opponents: ReadonlySet<number>;
  readonly hadBye: boolean;
}

export function rankPlayers(request: PairingRequest): Ranked[] {
  return request.players
    .filter((p) => p.available)
    .map((p) => {
      let score = p.acceleration ?? 0;
      const opponents = new Set<number>();
      let hadBye = false;
      for (const r of p.history) {
        score += pointsFor(r.result, request.pointSystem);
        if (r.opponent !== null && (isPlayedResult(r.result) || r.result === '*')) {
          opponents.add(r.opponent);
        }
        if (r.result === 'U' || r.result === 'F' || r.result === '+') hadBye = true;
      }
      return { p, score, opponents, hadBye };
    })
    .sort((a, b) => b.score - a.score || a.p.id - b.p.id);
}

/**
 * Danish Monrad: rank by score then pairing number, pair 1-2, 3-4, ... and
 * skip down the list to avoid rematches, backtracking when the rest of the
 * list cannot be completed. The lowest-ranked player without a previous bye
 * gets the bye when the count is odd.
 */
export class MonradEngine implements PairingEngine {
  readonly id = 'monrad';
  readonly name = 'Monrad (Danish)';

  pair(request: PairingRequest): PairingResult {
    const ranked = rankPlayers(request);
    const notes: string[] = [];
    const byeCandidates: (Ranked | null)[] =
      ranked.length % 2 === 1 ? [...ranked].reverse().filter((r) => !r.hadBye) : [null];
    if (ranked.length % 2 === 1 && byeCandidates.length === 0) {
      byeCandidates.push(ranked[ranked.length - 1] as Ranked);
      notes.push('every player has had a bye; the lowest-ranked player gets another');
    }
    for (const bye of byeCandidates) {
      const pool = ranked.filter((r) => r !== bye);
      const pairs = this.#search(pool);
      if (pairs !== null) {
        return {
          pairs: pairs.map(([a, b]) => this.#colour(a, b, request.initialColour)),
          byes: bye === null ? [] : [bye.p.id],
          notes,
        };
      }
    }
    throw new PairingError('no pairing without rematches exists');
  }

  #search(pool: readonly Ranked[]): [Ranked, Ranked][] | null {
    if (pool.length === 0) return [];
    const [first, ...rest] = pool as [Ranked, ...Ranked[]];
    for (let i = 0; i < rest.length; i++) {
      const opp = rest[i] as Ranked;
      if (first.opponents.has(opp.p.id)) continue;
      const remaining = rest.filter((_, j) => j !== i);
      const sub = this.#search(remaining);
      if (sub !== null) return [[first, opp], ...sub];
    }
    return null;
  }

  #colour(a: Ranked, b: Ranked, initialColour: Colour): Pair {
    const ca = allocateColours(
      colourState(a.p.history),
      colourState(b.p.history),
      a.p.id,
      initialColour,
    );
    return ca === 'w' ? { white: a.p.id, black: b.p.id } : { white: b.p.id, black: a.p.id };
  }
}
